import type { OrderProfileItem, RichOrderItem } from '@ps/api/types';

import type { OrderMode } from '../types';

/**
 * Row-level display helpers for an order in the list. Each is a small pure
 * function pulling the right value off a `RichOrderItem` (ported from RN's
 * `OrderItemCell` / `OrderListPage` logic).
 */

/** Product name, with the internal "Avatar Me" listing relabeled. */
export function orderProductName(item: RichOrderItem): string {
  const name = item.listingSnapshot?.name ?? 'Order';
  return name === 'Avatar Me' ? 'AI Creator Studio' : name;
}

/** Thumbnail (preferred) or default product image, if any. */
export function orderProductImage(item: RichOrderItem): string | undefined {
  const listing = item.listingSnapshot as
    | { thumbnailImage?: string; defaultImage?: string }
    | undefined;
  return listing?.thumbnailImage ?? listing?.defaultImage;
}

/** Product types that render an icon glyph instead of a product image. */
export type ProductIconType =
  | 'course'
  | 'tips'
  | 'avatar-me'
  | 'token_pack'
  | 'post'
  | null;

/**
 * Derive the icon glyph kind for a listing (RN's `getProductIconType`). The
 * DERIVED sub-types (course / tips / post) are distinguished via
 * `derivedFrom.from`, which is not in the typed model — read defensively.
 */
export function orderProductIconType(item: RichOrderItem): ProductIconType {
  const listing = item.listingSnapshot as
    | { type?: string; derivedFrom?: { from?: string } }
    | undefined;
  const type = listing?.type?.toUpperCase();
  const derivedFrom = listing?.derivedFrom?.from?.toUpperCase();
  if (type === 'DERIVED' && derivedFrom === 'SERIES') return 'course';
  if (type === 'DERIVED' && derivedFrom === 'TIP') return 'tips';
  if (type === 'DERIVED' && derivedFrom === 'POST') return 'post';
  if (type === 'AVATAR_ME') return 'avatar-me';
  if (type === 'TOKEN_PACK') return 'token_pack';
  return null;
}

/** The counterparty profile shown in the row, by viewing mode. */
export function orderRelatedProfile(
  item: RichOrderItem,
  mode: OrderMode,
): OrderProfileItem | undefined {
  return mode === 'buyer' ? item.seller : item.buyer;
}

/** Amount the buyer paid, preferring the buyer-currency breakdown. */
export function orderAmount(item: RichOrderItem): {
  amount: number;
  currency: string;
} {
  return {
    amount:
      item.buyerCurrencyOrderDetails?.buyerPaidAmount ??
      item.buyerPaidAmount ??
      item.total,
    currency: item.buyerCurrencyOrderDetails?.currency ?? 'usd',
  };
}

/** Refund total (when positive), preferring the buyer-currency breakdown. */
export function orderRefund(
  item: RichOrderItem,
): { amount: number; currency: string } | null {
  const amount =
    item.buyerCurrencyOrderDetails?.totalRefundIncludeSlaveOrders ??
    item.totalRefundIncludeSlaveOrders;
  if (amount == null || amount <= 0) return null;
  return {
    amount,
    currency: item.buyerCurrencyOrderDetails?.currency ?? 'usd',
  };
}
